import { useCallback, useEffect, useRef, useState } from 'react'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api, { apiUpload } from '../../../../../helpers/apiRequester'
import { BatchWidgetStep } from '../../../../reusable/batchWidgetCard/BatchWidgetCard'
import { BatchRun, count, formatSeconds } from '../../shared'
import {
    BatchStartResponse,
    BatchPreviewCoverage,
    LOST_CONTACT_AFTER_MS,
    RESUME_LOOKBACK,
    STALLED_AFTER_MS,
    STATUS_POLL_MS,
    applyBatchStartToOptimistic,
    createOptimisticBatchRun,
    isSpreadsheetName,
    progressFromBatch,
    etaMsRemaining,
    countdownMs,
    ProgressSample,
    ProgressPhase,
    phaseStepsFor,
    MOCK_FILE_NAME,
    MOCK_TICK_MS,
    mockProgressAt,
} from './viewThruBatchUploadUtils'

export const useViewThruBatchUpload = () => {
    const [file, setFile] = useState<File | null>(null)
    /** Survives a remount, where the File object does not. */
    const [fileName, setFileName] = useState<string | null>(null)
    const [origin, setOrigin] = useState('')
    const [originNames, setOriginNames] = useState<string[]>([])
    const [started, setStarted] = useState(false)
    const [progress, setProgress] = useState(0)
    const [running, setRunning] = useState(false)
    const [verifying, setVerifying] = useState(false)
    const [progressLabel, setProgressLabel] = useState('Processing clients')
    /** Which stretch the run is on, for the phase badges. Null when nothing is running. */
    const [phase, setPhase] = useState<ProgressPhase | null>(null)
    /** Pause before AffiliaTech when some identity-valid rows lack Title. */
    const [coverage, setCoverage] = useState<BatchPreviewCoverage | null>(null)
    const [runsReloadToken, setRunsReloadToken] = useState(0)
    const [optimisticRun, setOptimisticRun] = useState<BatchRun | null>(null)
    /** The estimate and when it was struck, so it can be counted down since. */
    const [eta, setEta] = useState<{ ms: number; at: number } | null>(null)
    /** The batch's current-attempt start, so elapsed survives a page reload. */
    const [runStartedAt, setRunStartedAt] = useState<number | null>(null)
    /** Seconds already banked from earlier attempts. Resume adds this onto the live clock. */
    const [bankedSeconds, setBankedSeconds] = useState(0)
    /** Ticks every second while running so elapsed does not sit still between polls. */
    const [now, setNow] = useState(() => Date.now())
    /** First poll that showed progress — see etaMsRemaining for why not created_at. */
    const anchorRef = useRef<ProgressSample | null>(null)
    /** Newest sample whose row count actually moved, which is what the rate measures against. */
    const lastProgressRef = useRef<ProgressSample | null>(null)
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
    /** Stops the resume lookup from overwriting a file the user just chose. */
    const interactedRef = useRef(false)
    /** When status calls started failing, so the card can stop claiming it is running. */
    const lostContactSinceRef = useRef<number | null>(null)
    /** Last heartbeat value seen and when, to spot a worker that stopped beating. */
    const heartbeatRef = useRef<{ value: string | null; seenAt: number } | null>(null)
    /** Drives the phase preview. Null whenever a real batch is what's on screen. */
    const mockRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const [mocking, setMocking] = useState(false)

    const stopMock = useCallback(() => {
        if (mockRef.current) clearInterval(mockRef.current)
        mockRef.current = null
    }, [])

    useEffect(() => stopMock, [stopMock])

    const stopPolling = useCallback(() => {
        if (pollRef.current) clearInterval(pollRef.current)
        pollRef.current = null
    }, [])

    useEffect(() => stopPolling, [stopPolling])

    /**
     * Drop the running card when we can no longer tell whether the batch is alive.
     * The batch may still be running server-side, so point the user at the history
     * list rather than claiming it failed.
     */
    const abandonWatch = useCallback(
        (reason: string) => {
            stopPolling()
            setRunning(false)
            setVerifying(false)
            setStarted(false)
            setOptimisticRun(null)
            setProgress(0)
            setProgressLabel('Processing clients')
            setPhase(null)
            anchorRef.current = null
            lastProgressRef.current = null
            setEta(null)
            setRunStartedAt(null)
            setBankedSeconds(0)
            setRunsReloadToken((token) => token + 1)
            Report.failure(
                'Batch stopped responding',
                `${reason} Check Previous batches for its final state, and use Resume if rows are still outstanding.`,
                'Okay'
            )
        },
        [stopPolling]
    )

    const openFilePicker = () => fileInputRef.current?.click()

    const acceptSpreadsheet = (next: File | null | undefined) => {
        if (!next || running || verifying || coverage) return
        if (!isSpreadsheetName(next.name)) {
            Report.failure('Wrong file type', 'Choose a CSV or Excel file (.csv, .xlsx, .xls).', 'Okay')
            return
        }
        interactedRef.current = true
        setFile(next)
        setFileName(next.name)
        setCoverage(null)
        setStarted(false)
        setProgress(0)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const handleFilesDropped = (files: File[]) => {
        acceptSpreadsheet(files[0])
    }

    const pollBatch = useCallback(
        async (batchId: string) => {
            try {
                const response = await api({ url: 'viewthru-batch-status', data: { batchId } })
                const batch = response.data?.batch as BatchRun | undefined
                if (!response.data?.success || !batch) {
                    throw new Error(response.data?.message || 'Could not load batch progress.')
                }

                lostContactSinceRef.current = null
                const next = progressFromBatch(batch)
                setProgress(next.progress)
                setProgressLabel(next.label)
                setPhase(next.phase)

                // From the poll, not optimisticRun — createOptimisticBatchRun stamps
                // created_at as now, which is wrong for a batch being resumed.
                const startedAt = batch.run_started_at || batch.created_at
                    ? new Date(batch.run_started_at || batch.created_at || '').getTime()
                    : NaN
                setRunStartedAt(Number.isFinite(startedAt) ? startedAt : null)
                setBankedSeconds(typeof batch.duration_seconds === 'number' ? batch.duration_seconds : 0)

                const latest: ProgressSample = {
                    at: Date.now(),
                    done: count(batch.processed_count) + count(batch.invalid_count),
                }
                // Anchor on the first sample with progress, so the dedupe/seed
                // stretch that completes no rows stays out of the measured rate.
                if (!anchorRef.current && latest.done > 0) anchorRef.current = latest

                // Only restrike the estimate when rows actually moved. Recomputing
                // on every poll would pair a moving clock with a stalled row count,
                // inflating the figure between the backend's 25-row checkpoints.
                if (!lastProgressRef.current || latest.done !== lastProgressRef.current.done) {
                    lastProgressRef.current = latest
                    const ms = etaMsRemaining(anchorRef.current, latest, count(batch.total_count))
                    setEta(ms == null ? null : { ms, at: latest.at })
                }

                // Compare heartbeats to each other rather than to the browser clock,
                // so a skewed local time cannot fake a stall.
                const beat = batch.heartbeat_at ?? batch.updated_at ?? null
                if (!heartbeatRef.current || heartbeatRef.current.value !== beat) {
                    heartbeatRef.current = { value: beat, seenAt: Date.now() }
                }

                if (batch.status === 'running') {
                    if (Date.now() - heartbeatRef.current.seenAt < STALLED_AFTER_MS) return false
                    abandonWatch('The batch has not reported any progress for several minutes.')
                    return true
                }

                stopPolling()
                setRunning(false)
                setOptimisticRun(null)
                setPhase(null)
                setRunsReloadToken((token) => token + 1)

                if (batch.status === 'failed') {
                    setStarted(false)
                    Report.failure('Batch failed', batch.error_message || 'The batch did not complete.', 'Okay')
                    return true
                }

                setFile(null)
                setFileName(null)
                setStarted(false)
                setProgress(0)
                setProgressLabel('Processing clients')
                if (fileInputRef.current) fileInputRef.current.value = ''
                return true
            } catch {
                // Keep polling through brief network/auth refresh failures, but stop
                // showing progress once the server has been unreachable for a while.
                const now = Date.now()
                if (lostContactSinceRef.current === null) lostContactSinceRef.current = now
                if (now - lostContactSinceRef.current < LOST_CONTACT_AFTER_MS) return false
                abandonWatch('The dashboard has not been able to reach the server for the last 30 seconds.')
                return true
            }
        },
        [abandonWatch, stopPolling]
    )

    const watchBatch = useCallback(
        async (batchId: string) => {
            lostContactSinceRef.current = null
            heartbeatRef.current = null
            // Per-watch, so a second run never measures against the first one's anchor.
            anchorRef.current = null
            lastProgressRef.current = null
            setEta(null)
            setRunStartedAt(null)
            setBankedSeconds(0)
            const finished = await pollBatch(batchId)
            if (finished) return
            pollRef.current = setInterval(() => {
                void pollBatch(batchId)
            }, STATUS_POLL_MS)
        },
        [pollBatch]
    )

    const attachToBatch = useCallback(
        (
            batch: Pick<
                BatchRun,
                | 'id'
                | 'filename'
                | 'origin'
                | 'total_count'
                | 'processed_count'
                | 'invalid_count'
                | 'environment'
                | 'status'
                | 'phase'
                | 'seeded_count'
            >
        ) => {
            stopPolling()
            interactedRef.current = true
            setFileName(batch.filename)
            if (batch.origin) setOrigin(batch.origin)
            setStarted(true)
            setVerifying(false)
            setRunning(true)
            setOptimisticRun({
                ...createOptimisticBatchRun(batch.filename, batch.origin ?? null),
                id: batch.id,
                total_count: batch.total_count,
                processed_count: batch.processed_count,
                invalid_count: batch.invalid_count,
                environment: batch.environment,
                status: 'running',
                phase: batch.phase,
                seeded_count: batch.seeded_count ?? 0,
            })
            const next = progressFromBatch(batch)
            setProgress(next.progress)
            setProgressLabel(next.label)
            setPhase(next.phase)
            void watchBatch(batch.id)
        },
        [stopPolling, watchBatch]
    )

    /**
     * Rebuild the running card for a batch that is still in flight, so coming
     * back to this page looks the same as never having left it.
     */
    useEffect(() => {
        let cancelled = false

        const resumeRunningBatch = async () => {
            try {
                const response = await api({ url: 'viewthru-batches', data: { limit: RESUME_LOOKBACK } })
                const batches = (response.data?.batches || []) as BatchRun[]
                const inFlight = batches.find((batch) => batch.status === 'running')
                if (cancelled || interactedRef.current || !inFlight) return

                setFileName(inFlight.filename)
                if (inFlight.origin) setOrigin(inFlight.origin)
                setStarted(true)
                setRunning(true)
                setProgressLabel('Processing clients')
                void watchBatch(inFlight.id)
            } catch {
                // No history route on this environment, or the request failed —
                // leave the card on the choose step.
            }
        }

        void resumeRunningBatch()
        return () => {
            cancelled = true
        }
    }, [watchBatch])

    useEffect(() => {
        let cancelled = false
        const loadOrigins = async () => {
            try {
                const response = await api({ url: 'viewthru-batch-origins', data: {} })
                const names = ((response.data?.origins || []) as { name?: string }[])
                    .map((row) => String(row.name || '').trim())
                    .filter(Boolean)
                if (!cancelled) setOriginNames(names)
            } catch {
                // Origin catalog is optional for resume; a typed name still saves.
            }
        }
        void loadOrigins()
        return () => {
            cancelled = true
        }
    }, [])

    const startBatchUpload = useCallback(
        async (originName: string, uploadFile: File) => {
            const formData = new FormData()
            formData.append('file', uploadFile)
            formData.append('origin', originName)

            const response = await apiUpload('viewthru-batch-credit-check', formData)
            const batch = response.data as BatchStartResponse

            if (!batch?.success || !batch.batchId) throw new Error(batch?.message || 'The batch did not start.')

            setCoverage(null)
            setVerifying(false)
            setRunning(true)
            const initialDone = count(batch.invalid)
            setProgress(batch.total > 0 ? Math.round((initialDone / batch.total) * 100) : 0)
            setProgressLabel(`${initialDone} of ${batch.total} rows`)
            // The runner opens with the duplicate check; the first poll corrects this.
            setPhase('deduping')
            setOptimisticRun(applyBatchStartToOptimistic(createOptimisticBatchRun(uploadFile.name, originName), batch))
            setOriginNames((names) =>
                names.includes(originName) ? names : [...names, originName].sort((a, b) => a.localeCompare(b))
            )

            await watchBatch(batch.batchId)
        },
        [watchBatch]
    )

    const runBatch = async () => {
        const originName = origin.trim()
        if (running || verifying || coverage || !file || !originName) return

        interactedRef.current = true
        stopPolling()
        setVerifying(true)
        setStarted(true)
        setRunning(false)
        setProgress(0)
        setProgressLabel('Checking rows and columns')

        try {
            const formData = new FormData()
            formData.append('file', file)

            const preview = await apiUpload('viewthru-batch-preview', formData)
            const next = preview.data as BatchPreviewCoverage
            if (!next?.success) throw new Error(next?.message || 'Could not check the spreadsheet.')

            if (next.missingTitle > 0 || next.invalid > 0 || (next.missingFields?.length ?? 0) > 0) {
                setCoverage(next)
                setVerifying(false)
                setStarted(false)
                return
            }

            await startBatchUpload(originName, file)
        } catch (error: any) {
            stopPolling()
            setStarted(false)
            setRunning(false)
            setVerifying(false)
            setCoverage(null)
            setOptimisticRun(null)
            setProgress(0)
            Report.failure(
                'Could not start batch',
                String(error?.response?.data?.message || error?.message || 'Could not run the batch credit check.').replace(
                    /\n/g,
                    '<br/>'
                ),
                'Okay'
            )
        }
    }

    const confirmContinue = async () => {
        const originName = origin.trim()
        if (running || verifying || !coverage || !file || !originName) return

        interactedRef.current = true
        stopPolling()
        setVerifying(true)
        setStarted(true)
        setRunning(false)
        setProgress(0)
        setProgressLabel('Starting batch')

        try {
            await startBatchUpload(originName, file)
        } catch (error: any) {
            stopPolling()
            setStarted(false)
            setRunning(false)
            setVerifying(false)
            setOptimisticRun(null)
            setProgress(0)
            // Keep the coverage panel so they can try Continue again.
            Report.failure(
                'Could not start batch',
                String(error?.response?.data?.message || error?.message || 'Could not run the batch credit check.').replace(
                    /\n/g,
                    '<br/>'
                ),
                'Okay'
            )
        }
    }

    const dismissCoverage = () => {
        if (running || verifying) return
        setCoverage(null)
        setStarted(false)
    }

    const resumeExistingBatch = async (run: BatchRun) => {
        if (running || verifying) return

        interactedRef.current = true
        stopPolling()
        setVerifying(false)
        setStarted(true)
        setRunning(true)
        setFileName(run.filename)
        if (run.origin) setOrigin(run.origin)
        setProgressLabel('Resuming remaining rows')
        setOptimisticRun({ ...run, status: 'running', finished_at: null, error_message: null })

        try {
            const response = await api({ url: 'viewthru-batch-resume', data: { batchId: run.id } })
            const batch = response.data as BatchStartResponse
            if (!batch?.success || !batch.batchId) {
                throw new Error(batch?.message || 'The batch could not be resumed.')
            }
            attachToBatch({
                id: batch.batchId,
                filename: run.filename,
                origin: run.origin,
                total_count: batch.total ?? run.total_count,
                processed_count: run.processed_count,
                invalid_count: batch.invalid ?? run.invalid_count,
                environment: batch.environment ?? run.environment,
                status: 'running',
            })
        } catch (error: any) {
            stopPolling()
            setStarted(false)
            setRunning(false)
            setOptimisticRun(null)
            Report.failure(
                'Could not resume batch',
                String(error?.response?.data?.message || error?.message || 'Could not resume this batch.'),
                'Okay'
            )
        }
    }

    const stopMockRun = useCallback(() => {
        stopMock()
        setMocking(false)
        setStarted(false)
        setRunning(false)
        setVerifying(false)
        setRunStartedAt(null)
        setBankedSeconds(0)
        setProgress(0)
        setProgressLabel('Processing clients')
        setPhase(null)
        setFileName(null)
    }, [stopMock])

    /**
     * Replay the phases against a fake row count. Nothing is uploaded and no
     * batch is created — this only exists to see how the card reads, since the
     * real dedupe/seed stretches take minutes and cost credits to reach.
     */
    const startMockRun = useCallback(() => {
        if (running || verifying || coverage) return
        interactedRef.current = true
        stopPolling()
        stopMock()

        const startedAt = Date.now()
        setMocking(true)
        setFile(null)
        setFileName(MOCK_FILE_NAME)
        setCoverage(null)
        setOptimisticRun(null)
        setEta(null)
        setBankedSeconds(0)
        setRunStartedAt(startedAt)
        setStarted(true)
        setVerifying(true)
        setRunning(false)
        setProgress(0)
        setProgressLabel('Checking rows and columns')
        setPhase(null)

        mockRef.current = setInterval(() => {
            const frame = mockProgressAt(Date.now() - startedAt)
            setNow(Date.now())
            if (frame.phase === 'done') {
                stopMockRun()
                return
            }
            setVerifying(frame.verifying || frame.phase === 'deduping')
            setRunning(frame.phase === 'seeding' || frame.phase === 'processing')
            setProgress(frame.progress)
            setProgressLabel(frame.label)
            setPhase(frame.phase === 'verifying' ? null : frame.phase)
        }, MOCK_TICK_MS)
    }, [coverage, running, stopMock, stopMockRun, stopPolling, verifying])

    const clearFile = () => {
        stopPolling()
        stopMockRun()
        setFile(null)
        setFileName(null)
        setCoverage(null)
        setStarted(false)
        setRunning(false)
        setVerifying(false)
        setOptimisticRun(null)
        setProgress(0)
        setProgressLabel('Processing clients')
        setPhase(null)
        setBankedSeconds(0)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const step: BatchWidgetStep =
        verifying || phase === 'deduping'
            ? 'verifying'
            : coverage
              ? 'confirm'
              : started
                ? 'running'
                : file
                  ? 'ready'
                  : 'choose'
    const busy = running || verifying || phase === 'deduping' || Boolean(coverage)
    const originName = origin.trim()

    // Polls are 2.5s apart, so elapsed needs its own clock to read as live.
    useEffect(() => {
        if (!running) return
        const timer = setInterval(() => setNow(Date.now()), 1_000)
        return () => clearInterval(timer)
    }, [running])

    const elapsedLabel = runStartedAt
        ? formatSeconds(bankedSeconds + (now - runStartedAt) / 1000)
        : null
    const remainingMs = eta ? countdownMs(eta.ms, eta.at, now) : null
    const remainingLabel = remainingMs == null ? null : formatSeconds(remainingMs / 1000)
    /** Counted all the way down, but the closing checkpoint has not landed yet. */
    const finishing = remainingMs === 0

    return {
        fileInputRef,
        fileName,
        origin,
        originNames,
        setOrigin,
        step,
        running,
        verifying,
        busy,
        canRun: Boolean(file) && Boolean(originName) && !busy,
        progress,
        progressLabel,
        phase,
        phaseSteps: phaseStepsFor(phase),
        coverage,
        elapsedLabel,
        remainingLabel,
        finishing,
        runsReloadToken,
        optimisticRun,
        openFilePicker,
        acceptSpreadsheet,
        handleFilesDropped,
        runBatch,
        confirmContinue,
        dismissCoverage,
        resumeExistingBatch,
        clearFile,
        mocking,
        startMockRun,
        stopMockRun,
    }
}
